import { inject, injectable } from "inversify";
import IContractService from "./IContractService";
import { ContractsModel } from "../../models/contracts/contractsModel";
import { ContractsApi } from "../api/ContractsApi";
import { OperationsApi } from "../api/OperationsApi";
import { SMSPacket } from "../../data/smsPacket";
import { ContractModel } from "../../models/contracts/contractModel";
import { IActionModel } from "../../models/IActionModel";
import { ActionStateEnum } from "../../models/enums/operStateEnum";
import { AccountDetailData } from "../../data/accountDetailData";
import { DeclarationPropertiesTypes } from "../../data/enums/declarationPropertiesTypes";
import { CreditScheduleModel } from "../../models/schedule/creditScheduleModel";
import { RequestParamsModel } from "../../models/statement/requestParamsModel";
import { OperationRequestData } from "../../data/operationRequestData";
import { DepositForecastItemModel } from "../../models/forecast/depositForecastItemModel";
import { OperationsHistoryApi } from "../api/OperationsHistoryApi";
import IAlertService from "../alert/IAlertService";
import { DiTokens } from "../diTokens";
import IErrorService from "../error/IErrorService";
import i18n from "@/i18n";

@injectable()
/**Сервис счетов */
export default class ContractService implements IContractService {
  constructor(
    @inject(DiTokens.AlertService) private alertService: IAlertService,
    @inject(DiTokens.ErrorService) private errorService: IErrorService
  ) {}
  /**
   * Получение списка объектов
   * @param ids Список Идентификаторов объектов для полного обновления
   */
  public async getList(ids: number[]): Promise<ContractsModel> {
    let groupContracts: ContractsModel = new ContractsModel();
    try {
      const contracts = await ContractsApi.getAllContractsAsync();
      groupContracts = new ContractsModel(contracts);

      if (ids.length) {
        for (const i in ids) {
          if (groupContracts.getContract(ids[i])) {
            const bankDetails = await ContractsApi.getAccountDetail(ids[i]);

            groupContracts.updateAccountDetails(ids[i], bankDetails);
          }
        }
      }
    } catch (e) {
      await this.errorService.errorHandler(e, true, true, "");
    }
    return groupContracts;
  }

  /**Получение реквизитов счета
   * * @param id Идентификатор запрашиваемого объекта
   */
  public async getBankDetails(id: number): Promise<AccountDetailData> {
    let accountDetail: AccountDetailData = new AccountDetailData();
    try {
      accountDetail = await ContractsApi.getAccountDetail(id);
    } catch (e) {
      await this.errorService.errorHandler(e, false, true, "");
    }
    return accountDetail;
  }

  /**
   * Получение одного объекта
   * @param id Идентификатор запрашиваемого объекта
   */
  public async get(id: number): Promise<ContractModel> {
    let contract: ContractModel = new ContractModel();
    try {
      const resp = await ContractsApi.getContractAsync(id);
      contract = new ContractModel(resp);
    } catch (e) {
      await this.errorService.errorHandler(e, false, true, "");
    }
    return contract;
  }
  /**
   * Изменение псевдонима объекта
   * @param id Идентификатор объекта для изменения алиаса
   * */
  public async editAlias(actionModel: IActionModel): Promise<IActionModel> {
    try {
      const id: number = actionModel.Data["contractId"] as number;
      const alias: string = actionModel.Data["alias"] as string;
      await ContractsApi.editContractAliasAsync(id, alias);
      actionModel._actionState = ActionStateEnum.Success;
    } catch (e) {
      await this.errorService.errorHandler(e, false, true, "");
      actionModel._actionState = ActionStateEnum.Error;
      actionModel.ActionStateText = e.message;
    }
    return actionModel;
  }

  /**Установка видимости объекта
   * @param id  Идентификатор объекта для изменения видимости
   * @param hidden Признак скрытости
   */
  public async setVisibility(actionModel: IActionModel, respondentCode: string): Promise<IActionModel> {
    try {
      const id: number = actionModel.Data["contractId"] as number;
      const hidden: boolean = !actionModel.Data["hidden"] as boolean;
      const ord: OperationRequestData = new OperationRequestData(null);
      ord.respondentCode = respondentCode;
      ord.setProperty(
        "contractId",
        id.toString(),
        DeclarationPropertiesTypes.Int
      );
      ord.setProperty(
        "hidden",
        hidden.toString(),
        DeclarationPropertiesTypes.Boolean
      );
      await OperationsApi.executeOperationSendAsync(ord);
      actionModel._actionState = ActionStateEnum.Success;
      return actionModel;
    } catch (e) {
      await this.errorService.errorHandler(e, false, true, "");
      actionModel._actionState = ActionStateEnum.Error;
      actionModel.ActionStateText = e.message;
    }
    return actionModel;
  }

  /**Отправка реквизитов счета */
  public async sendAccountDetail(
    actionModel: IActionModel
  ): Promise<IActionModel> {
    try {
      const id: number = actionModel.Data["contractId"] as number;
      const email: string = (actionModel.Data["email"] as string) ?? "";
      const phone: string = (actionModel.Data["phone"] as string) ?? "";
      await ContractsApi.sendAccountDetail(id, phone, email);
      actionModel._actionState = ActionStateEnum.Success;
      if (email && email.length > 0)
        this.alertService.success(i18n.t("main.contracts.messages.successEmail").toString());
      else
        this.alertService.success(i18n.t("main.contracts.messages.successPhone").toString());
    } catch (e) {
      await this.errorService.errorHandler(e, false, true, "");
      actionModel._actionState = ActionStateEnum.Error;
      actionModel.ActionStateText = e.message;
    }
    return actionModel;
  }

  public async getSmsPackets(): Promise<Array<SMSPacket>> {
    try {
      const packets: Array<SMSPacket> = await ContractsApi.getSMSpackets();
      if (packets !== undefined && packets !== null) {
        return packets.filter(
          (item: any) => item.Code >= 0
        );
      }
    } catch (e) {
      await this.errorService.errorHandler(e, false, true, "");
    }
    return [];
  }

  /** Получение доходности по вкладу
    @param contractId идентификатор вклада
   */
  public async getDepositForeCast(
    contractId: number
  ): Promise<Array<DepositForecastItemModel>> {
    let result: Array<DepositForecastItemModel> = [];

    try {
      result = await ContractsApi.getDepositForeCast(contractId);
    } catch (e) {
      await this.errorService.errorHandler(e, false, true, "");
    }

    return result;
  }

  public async getFile(id: number, code: string): Promise<any> {
    const ord: OperationRequestData = new OperationRequestData(null);
    let link: string = "";
    ord.respondentCode = code;
    ord.setProperty(
      "contractId",
      id.toString(),
      DeclarationPropertiesTypes.String
    );
    try {
      link = await OperationsHistoryApi.getHistoryPdf(ord);
    } catch (e) {
      await this.errorService.errorHandler(e, false, true, "");
    }
    return link;
  }

  public async getCreditSchedule(
    params: RequestParamsModel
  ): Promise<CreditScheduleModel> {
    try {
      const resModel: CreditScheduleModel = await OperationsHistoryApi.getCreditSchedule(
        params
      );
      return resModel;
    } catch (e) {
      await this.errorService.errorHandler(e, false, true, "");
    }
    return null;
  }
}
